import { useQuery } from '@tanstack/react-query';
import { usePatientCard } from '../../../../hooks/usePatientCard';
import type { VersionsFetchState, DocumentVersion } from '../review-surface/VersionedPdfPane';
import { fetchDxReportApprovalData } from './dxReportData';
import { resolveOrthopulseLevel, type OrthopulseLevel } from './orthopulseLevel';

const APPROVAL_DATA_STALE_MS = 60_000;

interface ApprovalReviewData {
    versions: DocumentVersion[];
    versionsState: VersionsFetchState;
    hasMoreVersions: boolean;
    orthopulseLevel: OrthopulseLevel | null;
    refetch: () => void;
}

// Dx report versions + fetch state, plus the OrthoPulse level resolved from
// it with a fallback to the patient's legacy Monday recommendation.
export function useApprovalReviewData(caseId: string): ApprovalReviewData {
    const {
        data: approvalData,
        isLoading,
        isError,
        refetch
    } = useQuery({
        queryKey: ['dx-report-approval-data', caseId],
        queryFn: () => fetchDxReportApprovalData(caseId),
        staleTime: APPROVAL_DATA_STALE_MS,
        enabled: Boolean(caseId)
    });
    const versionsState: VersionsFetchState = isError ? 'error' : isLoading ? 'loading' : 'ready';

    // Pre-Dx-report cases have no dx_report_submission event yet — fall back to
    // the patient's legacy Monday orthopulse recommendation.
    const { data: card } = usePatientCard(caseId);
    const orthopulseLevel = resolveOrthopulseLevel(
        approvalData?.orthopulseStatus ?? null,
        card?.orthopulse?.recommendation ?? null
    );

    return {
        versions: approvalData?.versions ?? [],
        versionsState,
        hasMoreVersions: approvalData?.hasMoreVersions ?? false,
        orthopulseLevel,
        refetch: () => void refetch()
    };
}
